<template>
  <div>
    <h1>Logs</h1>

    <div class="row mb-3 align-items-end">
      <div class="col-12 col-md-4 mb-2 mb-md-0">
        <label class="form-label">Log File</label>
        <select class="form-select" v-model="selectedLog" @change="fetchLog">
          <option v-for="l in availableLogs" :key="l.key" :value="l.key">
            {{ l.key }}
            <template v-if="l.size != null"> ({{ formatSize(l.size) }})</template>
          </option>
        </select>
      </div>
      <div class="col-6 col-md-2 mb-2 mb-md-0">
        <label class="form-label">Since</label>
        <select class="form-select" v-model="sinceHours" @change="fetchLog">
          <option :value="24">Last day</option>
          <option :value="48">Last 2 days</option>
          <option :value="72">Last 3 days</option>
          <option :value="168">Last week</option>
          <option :value="720">Last month</option>
          <option :value="0">All</option>
        </select>
      </div>
      <div class="col-6 col-md-2 mb-2 mb-md-0">
        <label class="form-label">Lines</label>
        <select class="form-select" v-model="lines" @change="fetchLog">
          <option :value="50">50</option>
          <option :value="200">200</option>
          <option :value="500">500</option>
          <option :value="1000">1000</option>
          <option :value="2000">2000</option>
          <option :value="5000">5000</option>
        </select>
      </div>
      <div class="col-12 col-md-4 d-flex gap-2">
        <button class="btn btn-primary w-100" @click="fetchLog" :disabled="loading">
          <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
          Refresh
        </button>
        <button class="btn btn-outline-secondary" @click="copyToClipboard" title="Copy to clipboard">
          <i class="bi bi-clipboard"></i>
        </button>
      </div>
    </div>

    <div v-if="error" class="alert alert-warning">{{ error }}</div>

    <div v-if="content" class="d-flex justify-content-between align-items-center text-muted small mb-1">
      <span>
        Showing last {{ displayedLines }}
        <template v-if="filteredLines != null"> of {{ filteredLines }} filtered</template>
        of {{ totalLines }} total lines
      </span>
      <span v-if="copied" class="text-success">Copied!</span>
    </div>

    <pre v-if="content" ref="logOutput" class="log-output bg-dark text-light p-3 rounded small">{{ content }}</pre>
    <div v-else-if="!loading && !error" class="text-muted fst-italic">No content.</div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import { useStore } from 'vuex'

const store = useStore()

const availableLogs = ref([])
const selectedLog = ref('esc-api-out')
const lines = ref(50)
const sinceHours = ref(24)
const content = ref('')
const totalLines = ref(0)
const filteredLines = ref(null)
const displayedLines = ref(0)
const loading = ref(false)
const error = ref(null)
const copied = ref(false)
const logOutput = ref(null)

async function fetchAvailableLogs() {
  try {
    const res = await store.state.api.get('/admin/logs')
    availableLogs.value = res.data.logs
  } catch (e) {
    error.value = 'Could not load log list: ' + (e.response?.data?.error || e.message)
  }
}

async function fetchLog() {
  if (!selectedLog.value) return
  loading.value = true
  error.value = null
  content.value = ''
  try {
    const params = { lines: lines.value }
    if (sinceHours.value > 0) params.since_hours = sinceHours.value
    const res = await store.state.api.get(`/admin/logs/${selectedLog.value}`, { params })
    content.value = res.data.content
    totalLines.value = res.data.total_lines
    filteredLines.value = res.data.filtered_lines ?? null
    displayedLines.value = res.data.lines
    await nextTick()
    if (logOutput.value) logOutput.value.scrollTop = logOutput.value.scrollHeight
  } catch (e) {
    error.value = e.response?.data?.error || e.message
  } finally {
    loading.value = false
  }
}

function formatSize(bytes) {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

async function copyToClipboard() {
  if (!content.value) return
  await navigator.clipboard.writeText(content.value)
  copied.value = true
  setTimeout(() => copied.value = false, 2000)
}

onMounted(async () => {
  await fetchAvailableLogs()
  await fetchLog()
})
</script>

<style scoped>
.log-output {
  max-height: 70vh;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-all;
  font-family: 'Courier New', monospace;
  font-size: 0.75rem;
  line-height: 1.4;
}
</style>
